import React, { HTMLProps, useMemo } from 'react';
import { Icon, IconsEnum } from 'components/Icons';
import { isNotEmpty, isTruthy, toSportUrlCase } from 'utility/functions';

import { AnchorLink } from 'components/anchorLink';
import { DynamicImage, enLoading } from 'components/Image';
import { ItemWithTranslation } from 'types/swagger';
import classNames from 'classnames';
import { isSnaiSite } from 'utility/constant';
import styles from './SportManifestazioneFallback.module.scss';
import { useTranslation } from 'hooks/useLingUI';

export type MenuManifestazioniItemType = ItemWithTranslation<{ text: string }> & { icon?: string; slug: string };

export type SportManifestazioneFallbackProps = {
  text?: string;
  icon?: IconsEnum;
  button?: { text: string; link?: string; action?: () => void };
  haveRightMargin?: boolean;
  manifestazioniList?: Array<MenuManifestazioniItemType>;
} & HTMLProps<HTMLElement>;

export const SportManifestazioneFallback = ({
  text,
  icon,
  button,
  className,
  haveRightMargin,
  manifestazioniList,
}: SportManifestazioneFallbackProps) => {
  const { t } = useTranslation();

  const objIcon = useMemo(() => {
    const src = icon ?? (isSnaiSite ? IconsEnum.SNAI_LOGO_SHORT : IconsEnum.HAPPYBET_LOGO);
    return <Icon iconId={src} className={styles.icon} color="var(--color-on-bg-primary)" />;
  }, [icon]);

  const { link, text: buttonText, action: ctaButton } = button ?? {};

  return (
    <div
      data-key="fallback-manifestazione"
      className={classNames(styles.container, className, { [styles.withMarginRight]: haveRightMargin })}
      suppressHydrationWarning
    >
      {objIcon}
      {text && <p className={styles.text}>{text}</p>}
      {isTruthy(manifestazioniList?.length) ? (
        <div className={styles.list} suppressHydrationWarning>
          {manifestazioniList!.map(({ textTrKey, text, slug, icon }, index) => {
            const txt = t(textTrKey, text);
            return (
              <AnchorLink className={styles.item} key={`${slug}-${index}`} href={toSportUrlCase(slug)}>
                {isNotEmpty(icon) && (
                  <div className={styles.iconContainer}>
                    <DynamicImage
                      src={`${icon}`}
                      alt={txt ?? ''}
                      fill
                      loading={enLoading.lazy}
                      decoding="async"
                      className={styles.iconFlag}
                    />
                  </div>
                )}
                {txt}
              </AnchorLink>
            );
          })}
        </div>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <React.Fragment>
          {link ? (
            <AnchorLink className={styles.button} href={toSportUrlCase(`/${link}`)}>
              {buttonText}
            </AnchorLink>
          ) : (
            ctaButton && (
              <button className={styles.button} onClick={ctaButton}>
                {buttonText}
              </button>
            )
          )}
        </React.Fragment>
      )}
    </div>
  );
};